import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ApolloDropdownComponent,
  DatepickerComponent,
  DropdownConfig,
  DropdownRowItem,
  ListItem,
  SelectListComponent,
  SelectListConfig,
} from '@degreed/apollo';
import { ScrollService } from '@degreed/lxp/services';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { Trigger, TriggerSchedule, TriggerScheduleType } from '../../model';
import { restoreRequiredSelection } from '../../utils';
import {
  isDayRequired,
  isSpecifiedDateRequired,
  makeCadenceOptions,
  makeMonthlyDayOptions,
  makeTimeOptions,
  makeWeeklyDayOptions,
} from '../../utils/schedule-util';
import { TriggerBackCancelSaveButtonsComponent } from '../trigger-back-cancel-save-buttons/trigger-back-cancel-save-buttons.component';

@Component({
  selector: 'dgx-trigger-schedule-form',
  templateUrl: './trigger-schedule-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ApolloDropdownComponent,
    DatepickerComponent,
    ReactiveFormsModule,
    SelectListComponent,
    TriggerBackCancelSaveButtonsComponent,
  ],
  providers: [ScrollService],
})
export class TriggerScheduleFormComponent implements OnInit {
  // Inputs
  public readonly trigger = input.required<Trigger>();
  public readonly isSaving = input(false);

  // Outputs
  public readonly cancelEmitter = output<void>();
  public readonly saveEmitter = output<Trigger>();
  public readonly backEmitter = output<void>();

  @ViewChild('cadenceSelect', { read: ElementRef })
  public cadenceControlRef: ElementRef<HTMLElement>;
  @ViewChild('daySelect', { read: ElementRef })
  public dayControlRef: ElementRef<HTMLElement>;
  @ViewChild('dateSelect')
  public specifiedDateControlRef: DatepickerComponent;
  @ViewChild('timeSelect', { read: ElementRef })
  public timeControlRef: ElementRef<HTMLElement>;

  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly i18n = this.translate.instant([
    'Automation_Schedule_Cadence',
    'Automation_Schedule_Date',
    'Automation_Schedule_Day',
    'Automation_Schedule_TimeUTC',
    'Automation_Schedule_SelectCadence',
    'Automation_Schedule_SelectDate',
    'Automation_Schedule_SelectDay',
    'Automation_Schedule_SelectTime',
  ]);
  public readonly i18nShared = this.translate.instant([
    'Core_NoResults',
    'Core_FieldRequired',
    'Core_Back',
    'Core_Save',
    'Core_Cancel',
    'Core_Search',
    'Core_Loading',
  ]);
  public readonly TriggerScheduleType = TriggerScheduleType;
  public cadenceOptions = makeCadenceOptions(this.translate);
  public weeklyDayOptions = makeWeeklyDayOptions(this.translate);
  public monthlyDayOptions = makeMonthlyDayOptions(this.translate);
  public timeOptions = makeTimeOptions();

  public readonly cadenceDropdownConfig: DropdownConfig = {
    id: 'schedule-cadence-dropdown',
    label: this.i18n.Automation_Schedule_Cadence,
    required: true,
  };
  public readonly cadenceSelectListConfig: SelectListConfig = {
    id: 'schedule-cadence-list',
    options: this.cadenceOptions as unknown as Record<string, unknown>[],
    ariaLabel: this.i18n.Automation_Schedule_Cadence,
  };
  public readonly dayDropdownConfig: DropdownConfig = {
    id: 'schedule-day-dropdown',
    label: this.i18n.Automation_Schedule_Day,
    required: true,
  };
  public readonly weeklyDaySelectListConfig: SelectListConfig = {
    id: 'schedule-day-weekly-list',
    options: this.weeklyDayOptions as unknown as Record<string, unknown>[],
    ariaLabel: this.i18n.Automation_Schedule_Day,
  };
  public readonly monthlyDaySelectListConfig: SelectListConfig = {
    id: 'schedule-day-monthly-list',
    options: this.monthlyDayOptions as unknown as Record<string, unknown>[],
    ariaLabel: this.i18n.Automation_Schedule_Day,
  };
  public readonly timeDropdownConfig: DropdownConfig = {
    id: 'schedule-time-dropdown',
    label: this.i18n.Automation_Schedule_TimeUTC,
    required: true,
  };
  public readonly timeSelectListConfig: SelectListConfig = {
    id: 'schedule-time-list',
    options: this.timeOptions as unknown as Record<string, unknown>[],
    ariaLabel: this.i18n.Automation_Schedule_TimeUTC,
  };
  public readonly isInvalid = signal(false);
  public isDayRequired = isDayRequired;
  public isSpecifiedDateRequired = isSpecifiedDateRequired;

  public triggerForm: FormGroup;
  /** Minimum selectable date — preserves legacy `[isMinDateToday]` behavior. */
  public readonly todayDate = DateTime.now().startOf('day');

  public get timeControl() {
    return this.triggerForm.get('time') as FormControl;
  }

  public get cadenceControl() {
    return this.triggerForm.get('cadence') as FormControl;
  }

  public get dayControl() {
    return this.triggerForm.get('day') as FormControl;
  }

  public get specifiedDateControl() {
    return this.triggerForm.get('specifiedDate') as FormControl;
  }

  public ngOnInit(): void {
    const triggerVal = this.trigger();
    if (!triggerVal.schedule) {
      triggerVal.schedule = {} as TriggerSchedule;
    }

    this.triggerForm = new FormGroup({
      cadence: new FormControl(triggerVal.schedule.scheduleType, [
        Validators.required,
      ]),
      day: new FormControl(
        triggerVal.schedule.day,
        this.isDayRequired(triggerVal.schedule.scheduleType)
          ? [Validators.required]
          : null
      ),
      // da-datepicker's CVA expects DateTime; the server returns ISO string.
      specifiedDate: new FormControl(
        this.toDateTime(triggerVal.schedule.specifiedDate),
        this.isSpecifiedDateRequired(triggerVal.schedule.scheduleType)
          ? [Validators.required]
          : null
      ),
      time: new FormControl(triggerVal.schedule.executionTime, [
        Validators.required,
      ]),
    });

    this.cadenceControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: TriggerScheduleType) => {
        // Reset the dependent fields whose value space is cadence-specific
        // (day-of-week vs day-of-month, specific date for Once). Time is
        // universal across cadences, so leave it alone so users don't have to
        // re-pick it after every cadence change.
        this.dayControl.setValue(null);
        this.dayControl.clearValidators();

        this.specifiedDateControl.setValue(null);
        this.specifiedDateControl.clearValidators();

        if (this.isDayRequired(value))
          this.dayControl.setValidators([Validators.required]);

        if (this.isSpecifiedDateRequired(value))
          this.specifiedDateControl.setValidators([Validators.required]);

        this.dayControl.updateValueAndValidity();
        this.specifiedDateControl.updateValueAndValidity();
      });
  }

  /** New dropdown emits ListItem[]; treat single-select as items[0]. */
  public setCadence(
    items: ListItem[],
    dropdown: ApolloDropdownComponent,
    selectList: SelectListComponent
  ) {
    const id = items?.[0]?.id;
    if (id == null) {
      restoreRequiredSelection(
        dropdown,
        selectList,
        this.selectedCadenceItems()
      );
      return;
    }
    if (id === this.cadenceControl.value) return;
    this.cadenceControl.setValue(id);
  }

  public setDay(
    items: ListItem[],
    dropdown: ApolloDropdownComponent,
    selectList: SelectListComponent
  ) {
    const id = items?.[0]?.id;
    if (id == null) {
      restoreRequiredSelection(dropdown, selectList, this.selectedDayItems());
      return;
    }
    this.dayControl.setValue(id);
  }

  public setTime(
    items: ListItem[],
    dropdown: ApolloDropdownComponent,
    selectList: SelectListComponent
  ) {
    const id = items?.[0]?.id;
    if (id == null) {
      restoreRequiredSelection(dropdown, selectList, this.selectedTimeItems());
      return;
    }
    this.timeControl.setValue(id);
  }

  /**
   * The legacy form stored specifiedDate as a JS Date string; Apollo's
   * datepicker writes Luxon DateTime via CVA. Normalize to a JS Date on the
   * form-control side so the API payload shape stays the same.
   */
  public toDateTime(raw: unknown): DateTime | null {
    if (!raw) return null;
    if (raw instanceof DateTime) return raw.isValid ? raw : null;
    if (raw instanceof Date) {
      const dt = DateTime.fromJSDate(raw);
      return dt.isValid ? dt : null;
    }
    if (typeof raw === 'string') {
      const dt = DateTime.fromISO(raw);
      return dt.isValid ? dt : null;
    }
    return null;
  }

  /**
   * Memoized ListItem[] for da-dropdown's [value] binding. Stable references
   * across CD passes prevent the dropdown's selectedItems sync effect from
   * re-firing on every cycle.
   */
  private selectionCache(
    pool: DropdownRowItem[],
    value: unknown,
    cache: { value?: unknown; items?: ListItem[] }
  ): ListItem[] {
    if (cache.value === value && cache.items) return cache.items;
    const match = pool.find((o) => o.id === value);
    cache.value = value;
    cache.items = match ? [match as ListItem] : [];
    return cache.items;
  }
  private cadenceCache: { value?: unknown; items?: ListItem[] } = {};
  private timeCache: { value?: unknown; items?: ListItem[] } = {};
  private dayCache: { value?: unknown; items?: ListItem[] } = {};

  public selectedCadenceItems(): ListItem[] {
    return this.selectionCache(
      this.cadenceOptions,
      this.cadenceControl?.value,
      this.cadenceCache
    );
  }
  public selectedTimeItems(): ListItem[] {
    return this.selectionCache(
      this.timeOptions,
      this.timeControl?.value,
      this.timeCache
    );
  }
  public selectedDayItems(): ListItem[] {
    const pool =
      this.cadenceControl?.value === TriggerScheduleType.Monthly
        ? this.monthlyDayOptions
        : this.weeklyDayOptions;
    return this.selectionCache(pool, this.dayControl?.value, this.dayCache);
  }

  public onSave(): void {
    this.isInvalid.set(!this.validateTrigger());
    if (this.isInvalid()) {
      this.handleErrors();
      return;
    }

    const { cadence, day, specifiedDate, time } = this.triggerForm.value;
    // Convert back to JS Date for the API payload (legacy shape).
    const jsDate =
      specifiedDate instanceof DateTime
        ? specifiedDate.toJSDate()
        : specifiedDate;
    const triggerVal = this.trigger();
    this.saveEmitter.emit({
      ...triggerVal,
      schedule: {
        ...triggerVal.schedule,
        scheduleType: cadence,
        day: day,
        specifiedDate: jsDate,
        executionTime: time,
      },
    });
  }

  public onCancel(): void {
    this.cancelEmitter.emit();
  }

  public onBack(): void {
    this.backEmitter.emit();
  }

  public handleErrors(): void {
    setTimeout(() => {
      const firstInvalidControl = Object.keys(this.triggerForm.controls).find(
        (controlName) => this.triggerForm.get(controlName)?.invalid
      );

      switch (firstInvalidControl) {
        case 'cadence':
          this.focusDropdownButton(this.cadenceControlRef);
          break;
        case 'day':
          this.focusDropdownButton(this.dayControlRef);
          break;
        case 'specifiedDate':
          this.specifiedDateControlRef?.calendarButtonRef.nativeElement.focus();
          break;
        case 'time':
          this.focusDropdownButton(this.timeControlRef);
          break;
        default:
          break;
      }
    });
  }

  private validateTrigger(): boolean {
    this.triggerForm.markAllAsTouched();
    return this.triggerForm.valid;
  }

  /** ApolloDropdownComponent's button ref is private, so reach in via the host. */
  private focusDropdownButton(host: ElementRef<HTMLElement> | undefined) {
    host?.nativeElement.querySelector<HTMLButtonElement>('button')?.focus();
  }
}
